import React, { useEffect, useRef } from 'react';

import { useApiClient } from '@eluve/api-client-provider';
import {
  Button,
  DetailsSidebar,
  DetailsSidebarTrigger,
  Icon,
  Stack,
  Text,
  Textarea,
  useToast,
} from '@eluve/components';
import {
  useAppointmentId,
  useSummary,
} from '@eluve/frontend-appointment-hooks';
import { useTranslation } from '@eluve/i18n';
import { useNamedLogger } from '@eluve/logger';
import { useCaptureEvent } from '@eluve/posthog-react';
import { useTenantIdFromParams } from '@eluve/session-helpers';

type CustomInstructionsProps = {
  instructions: string;
  setInstructions: (instructions: string) => void;
  disabled?: boolean;
};

export const CustomInstructions: React.FC<CustomInstructionsProps> = ({
  instructions,
  setInstructions,
  disabled,
}) => {
  const { t } = useTranslation();
  const toast = useToast();
  const appointmentId = useAppointmentId();
  const logger = useNamedLogger('CustomInstructions');
  const textareaRef = useRef<HTMLTextAreaElement | null>(null);
  const apiClient = useApiClient();
  const currentSummary = useSummary();
  const tenantId = useTenantIdFromParams();
  const didUserTypeInstruction = useRef<boolean>(false);

  const captureEvent = useCaptureEvent({
    tenantId,
    appointmentId,
  });

  const exampleChips = [
    t('smartBlocks.customInstructions.chipRemoveParts'),
    t('smartBlocks.customInstructions.chipIncludeDiscussion'),
    t('smartBlocks.customInstructions.chipMoreConcise'),
    t('smartBlocks.customInstructions.chipMoreDetails'),
  ];
  useEffect(() => {
    textareaRef.current?.focus();
  }, []);

  const handleButtonClick = (instruction: string) => {
    captureEvent('user_clicked_regenerate_note_example_improvement_chip', {
      instruction,
    });

    const newInstructions = `${instructions ? `${instructions}\n` : ''}${instruction} `;
    setInstructions(newInstructions);
    textareaRef.current?.focus();
  };

  const handleRegenerate = async () => {
    logger.info('Regenerating summary with custom instructions', {
      appointmentId,
    });

    captureEvent('user_regenerated_note_with_custom_instructions', {
      instructions,
    });

    try {
      const result =
        await apiClient.llm.resummarizeAppointmentWithUserInstructions({
          body: {
            llmOutputId: currentSummary.llmOutputId!,
            userInstruction: instructions,
          },
          params: {
            tenantId: tenantId!,
            appointmentId,
          },
        });

      if (result.status !== 201) {
        throw new Error(
          'Failed to start summarization job with custom instructions',
        );
      }

      toast.success(t('smartBlocks.customInstructions.regenerating'));
    } catch (e) {
      toast.error(t('smartBlocks.customInstructions.regenerateFailed'));
    }

    return;
  };

  return (
    <>
      <DetailsSidebar
        size="lg"
        title={t('smartBlocks.customInstructions.title')}
        description={t('smartBlocks.customInstructions.description')}
      >
        <Stack className="p-8" align="stretch">
          <Textarea
            ref={textareaRef} // Attach the ref to the Textarea
            placeholder={t('smartBlocks.customInstructions.placeholder')}
            className="min-h-[120px]"
            value={instructions}
            onChange={(e) => {
              if (!didUserTypeInstruction.current) {
                captureEvent('user_typed_improve_note_text');
                didUserTypeInstruction.current = true;
              }
              setInstructions(e.target.value);
            }}
          />
          <Stack className="py-8" gap={3} align="stretch">
            <Text color="tertiary">
              {t('smartBlocks.customInstructions.examples')}
            </Text>
            {exampleChips.map((instruction, index) => (
              <div key={index}>
                <Button
                  variant="outline"
                  size="xs"
                  onClick={() => handleButtonClick(instruction)}
                >
                  {instruction}
                </Button>
              </div>
            ))}
          </Stack>
          <div className="pb-6">
            <Text>{t('smartBlocks.customInstructions.appliesToThisNote')}</Text>
          </div>

          <Stack direction="horizontal" justify="end" align="stretch">
            <DetailsSidebarTrigger>
              <Button disabled={!instructions} onClick={handleRegenerate}>
                {t('smartBlocks.customInstructions.regenerateNote')}
              </Button>
            </DetailsSidebarTrigger>
          </Stack>
        </Stack>
      </DetailsSidebar>
      <DetailsSidebarTrigger>
        <Button
          variant="outline"
          size="sm"
          disabled={disabled}
          onClick={() => {
            captureEvent('user_opened_improve_note_dialog');
          }}
        >
          <Icon name="WandSparkles" size="auto" />
          {t('smartBlocks.customInstructions.title')}
        </Button>
      </DetailsSidebarTrigger>
    </>
  );
};
